import React from "react";

/**
 * Pagination Component
 * Usage:
 *   const [page, setPage] = useState(1);
 *   const ITEMS_PER_PAGE = 12;
 *   const paginated = sortedData.slice((page-1)*ITEMS_PER_PAGE, page*ITEMS_PER_PAGE);
 *   <Pagination current={page} total={sortedData.length} perPage={ITEMS_PER_PAGE} onChange={setPage} />
 */
function Pagination({ current, total, perPage, onChange }) {
  const totalPages = Math.ceil(total / perPage);
  if (totalPages <= 1) return null;

  const getPages = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (current > 3) pages.push("...");
      for (let i = Math.max(2, current - 1); i <= Math.min(totalPages - 1, current + 1); i++) {
        pages.push(i);
      }
      if (current < totalPages - 2) pages.push("...");
      pages.push(totalPages);
    }
    return pages;
  };

  const btnStyle = (active) => ({
    width: "38px",
    height: "38px",
    borderRadius: "8px",
    border: active ? "none" : "1px solid #e5e7eb",
    backgroundColor: active ? "#0d9488" : "white",
    color: active ? "white" : "#374151",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "600",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.15s"
  });

  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", padding: "30px 0" }}>
      {/* Prev */}
      <button
        onClick={() => onChange(current - 1)}
        disabled={current === 1}
        style={{
          ...btnStyle(false),
          opacity: current === 1 ? 0.4 : 1,
          cursor: current === 1 ? "not-allowed" : "pointer"
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="15 18 9 12 15 6"/>
        </svg>
      </button>

      {/* Pages */}
      {getPages().map((page, i) =>
        page === "..." ? (
          <span key={`dots-${i}`} style={{ padding: "0 4px", color: "#9ca3af", fontSize: "14px" }}>
            ...
          </span>
        ) : (
          <button
            key={page}
            onClick={() => onChange(page)}
            style={btnStyle(page === current)}
            onMouseEnter={e => {
              if (page !== current) {
                e.target.style.borderColor = "#0d9488";
                e.target.style.color = "#0d9488";
              }
            }}
            onMouseLeave={e => {
              if (page !== current) {
                e.target.style.borderColor = "#e5e7eb";
                e.target.style.color = "#374151";
              }
            }}
          >
            {page}
          </button>
        )
      )}

      {/* Next */}
      <button
        onClick={() => onChange(current + 1)}
        disabled={current === totalPages}
        style={{
          ...btnStyle(false),
          opacity: current === totalPages ? 0.4 : 1,
          cursor: current === totalPages ? "not-allowed" : "pointer"
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="9 18 15 12 9 6"/>
        </svg>
      </button>

      {/* Info */}
      <span style={{ marginLeft: "12px", fontSize: "13px", color: "#6b7280" }}>
        Page {current} / {totalPages}
      </span>
    </div>
  );
}

export default Pagination;
